import * as _ from 'lodash';

import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension/logOnlyInProduction';
import thunk from 'redux-thunk';

import { uploadRootReducer } from './model/uploadRootReducer.model';

import * as WorksheetsReducer from './model/worksheetsReducer.model';

interface Worksheet {
  name: string;
  summaryData: any[][];
  summaryNumberOfRows: number;
}

export interface IMapping {
  headerName: string;
  columnName: string;
  columnHeader?: string;
  mapFieldName?: string;
}

export default class UploadModelService {
  store;
  uploadApi;

  constructor() {
    'ngInject';

    let initialState = {
      // navigation: null,
      // worksheets: null,
      // columns: null,
      // file: null
    };

    this.store = createStore(
      uploadRootReducer,
      initialState,
      composeWithDevTools(applyMiddleware(thunk))
    );
  }

  setUploadApi(uploadApi) {
    this.uploadApi = uploadApi;
  }

  resetState() {
    this.store.dispatch({
      type: 'RESET_STATE'
    });
  }

  getSelectedWorksheet(state) {
    // why do we always pass state in?
    let selectedWorksheetName = _.get(
      state.worksheets,
      'selectedWorksheetName'
    );
    let worksheets: any = _.get(state.worksheets, 'worksheets');

    return _.find(worksheets, (worksheet: any) => {
      return worksheet.name === selectedWorksheetName;
    });
  }

  canSearchProducts(state) {
    return !!_.get(state.file, 'uploadId') && this.isTabComplete('columns');
  }

  getSearchProductsError(state) {
    if (!_.get(state.file, 'uploadId')) {
      return 'No File Selected';
    } else if (!this.isTabComplete('columns')) {
      return 'Columns must be mapped first';
    } else {
      return null;
    }
  }

  hasItemsToImport() {
    let state = this.store.getState();
    return !!_.get(state.searchResults, 'recordMatchedCount');
  }

  isTabComplete(name) {
    let state = this.store.getState();

    switch (name) {
      case 'file':
        return _.get(state, 'file.isReady');

      case 'tabs':
        return _.get(state.worksheets, 'selectedWorksheetName');

      case 'columns':
        // Show it as not complete if we haven't loaded the data yet
        if (!_.get(state.columns, 'data')) {
          return false;
        }

        let selectedWorksheet = this.getSelectedWorksheet(state);
        let requiredColumns = _.map(
          _.filter(
            _.get(state.columns, 'data'),
            (column: any) => column.required
          ),
          'name'
        );
        let mappedColumns = _.map(
          _.get(selectedWorksheet, 'columnMappings'),
          'columnName'
        );
        let needsMapping = _.without(requiredColumns, ...mappedColumns);

        return needsMapping.length == 0;

      case 'summary':
        return _.get(state, 'searchResults'); // if search results have been loaded, show the import button

      default:
        return false;
    }
  }

  isActiveTab(tabName) {
    return _.get(this.store.getState().navigation, 'activeTab') === tabName;
  }

  // Action Creators

  updateActiveTab(activeTab) {
    return {
      type: 'UPDATE_ACTIVE_TAB',
      data: {
        activeTab
      }
    };
  }

  goToPreviousTab() {
    let currentTab = _.get(this.store.getState().navigation, 'activeTab');

    switch (currentTab) {
      case 'tabs':
        return this.updateActiveTab('file');

      case 'columns':
        return this.updateActiveTab('tabs');

      case 'summary':
        return this.updateActiveTab('columns');

      // Not sure this is helpful. Maybe better to just throw an exception?
      default:
        return this.updateActiveTab('file');
    }
  }

  goToNextTab() {
    let currentTab = _.get(this.store.getState().navigation, 'activeTab');

    switch (currentTab) {
      case 'file':
        return this.updateActiveTab('tabs');

      case 'tabs':
        return this.updateActiveTab('columns');

      case 'columns':
        return this.updateActiveTab('summary');

      // Not sure this is helpful. Maybe better to just throw an exception?
      default:
        return this.updateActiveTab('file');
    }
  }

  // Column Actions

  loadOfferColumns() {
    return {
      type: 'LOAD_OFFER_COLUMNS'
    };
  }

  offerColumnsLoaded(columns) {
    return {
      type: 'OFFER_COLUMNS_LOADED',
      data: columns
    };
  }

  // File Actions

  selectFile(file) {
    return {
      type: 'SELECT_FILE',
      data: file
    };
  }

  setFileError(error: string) {
    return {
      type: 'SET_FILE_ERROR',
      data: {
        error
      }
    };
  }

  setFileReady(isReady: boolean) {
    return {
      type: 'SET_FILE_READY',
      data: {
        isReady
      }
    };
  }

  uploadFile(file) {
    return {
      type: 'UPLOAD_FILE',
      data: file
    };
  }

  whenFileUploaded(fileId: number) {
    return {
      type: 'FILE_IS_UPLOADED',
      data: fileId
    };
  }

  // Worksheet Actions
  updateWorksheets(worksheets: Worksheet[]) {
    return {
      type: WorksheetsReducer.UPDATE_WORKSHEETS,
      data: worksheets
    };
  }

  updateSelectedWorksheet(worksheet: Worksheet) {
    return {
      type: WorksheetsReducer.UPDATE_SELECTED_WORKSHEET,
      data: worksheet
    };
  }

  // This should work once we add redux-thunk
  fetchWorksheetHeaders(worksheet: Worksheet, uploadId) {
    let state = this.store.getState();

    return (dispatch) => {
      dispatch(this.loadWorksheetHeaders(worksheet));

      // Get the updated worksheet
      state = this.store.getState();
      worksheet = this.getSelectedWorksheet(state);

      let headerRows = _.map(worksheet.summaryData, (rowData, index) => {
        return {
          values: rowData || [],
          // FIXME: we should use the rowIndex provided by the parser
          // to avoid issues with skipped rows
          rowIndex: index
        };
      });

      return this.uploadApi
        .parseHeaders(uploadId, headerRows)
        .then((headers) => {
          let rowIndex = headers.headerIndex;
          let rows = _.map(headers.rows, (row: any) => row.headerColumns);

          dispatch(this.worksheetHeadersLoaded(worksheet, rowIndex, rows));

          // Get the updated worksheet
          state = this.store.getState();
          worksheet = this.getSelectedWorksheet(state);

          let mappings: IMapping[] = rows[rowIndex];

          // We only care about the mappings we can use
          mappings = _.filter(mappings, (mapping) => {
            return mapping.columnHeader != null && mapping.mapFieldName != null;
          });

          mappings = _.map(mappings, (mapping) => {
            return {
              headerName: mapping.columnHeader,
              columnName: mapping.mapFieldName
            };
          });

          dispatch(this.updateWorksheetColumnMappings(worksheet, mappings));
        })
        .catch((response) => {
          dispatch(
            this.worksheetHeadersLoadError(worksheet, response.data.message)
          );
        });
    };
  }

  loadWorksheetHeaders(worksheet: Worksheet) {
    return {
      type: WorksheetsReducer.LOAD_HEADERS,
      data: {
        worksheet
      }
    };
  }

  worksheetHeadersLoaded(worksheet: Worksheet, rowIndex: number, rows) {
    return {
      type: WorksheetsReducer.HEADERS_LOADED,
      data: {
        worksheet,
        rowIndex,
        rows
      }
    };
  }

  worksheetHeadersLoadError(worksheet: Worksheet, error: string) {
    return {
      type: WorksheetsReducer.HEADERS_LOAD_ERROR,
      data: {
        worksheet,
        error
      }
    };
  }

  updateWorksheetRowIndex(worksheet: Worksheet, rowIndex) {
    return {
      type: WorksheetsReducer.UPDATE_ROW_INDEX,
      data: {
        worksheet,
        rowIndex
      }
    };
  }

  updateWorksheetColumnMappings(worksheet: Worksheet, columnMappings) {
    return {
      type: WorksheetsReducer.UPDATE_COLUMN_MAPPINGS,
      data: {
        worksheet,
        columnMappings
      }
    };
  }

  // Search Results

  updateSearchResults(searchResults) {
    return {
      type: 'UPDATE_SEARCH_RESULTS',
      data: searchResults
    };
  }
}
